import { Row, Col, theme, Tag } from "antd";
import {
  getAvailableMetrics,
  DynamicMetrics,
  MetricsConfig,
} from "./DynamicMetrics";
import { StandardCard } from "./StandardCard";
import { CompoundStatistic } from "./CompoundStatistic";
import { formatDuration } from "src/utils/duration";
import { CustomTooltip } from "src/components/CustomTooltip";
import { ReactNode } from "react";
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  FieldTimeOutlined,
  NumberOutlined,
  PercentageOutlined,
  DollarOutlined,
  StarOutlined,
  TeamOutlined,
  BranchesOutlined,
} from "@ant-design/icons";

interface MetricsDisplayProps {
  entity: any;
  maxMetrics?: number;
  config?: MetricsConfig;
  className?: string;
  style?: React.CSSProperties;
}

// Map metric types to icons
const metricIcons = {
  duration: <ClockCircleOutlined />,
  number: <NumberOutlined />,
  percentage: <PercentageOutlined />,
  currency: <DollarOutlined />,
  size: <StarOutlined />,
  count: <TeamOutlined />,
  score: <CheckCircleOutlined />,
  classification: <BranchesOutlined />,
  urgency: <FieldTimeOutlined />,
};

// Map classification values to colors
const classificationColors = {
  Feature: "#1890ff",
  Bug: "#f5222d",
  Task: "#52c41a",
  Improvement: "#722ed1",
  Documentation: "#faad14",
  Other: "#8c8c8c",
};

// Map urgency values to colors
const urgencyColors = {
  High: "#f5222d",
  Medium: "#faad14",
  Low: "#52c41a",
  None: "#8c8c8c",
};

export const MetricsDisplay: React.FC<MetricsDisplayProps> = ({
  entity,
  maxMetrics = 3,
  config,
  className,
  style,
}) => {
  const { token } = theme.useToken();
  const selectedMetrics = getAvailableMetrics(entity, config);
  const displayMetrics = selectedMetrics.slice(0, maxMetrics);

  // Calculate column sizes based on number of metrics
  const getColSpan = (totalMetrics: number) => {
    switch (totalMetrics) {
      case 1: {
        return { xs: 24, sm: 24, md: 24 };
      }
      case 2: {
        return { xs: 24, sm: 12, md: 12 };
      }
      default: {
        return { xs: 24, sm: 12, md: 8 };
      }
    }
  };

  const colSpan = getColSpan(displayMetrics.length);

  const getFullPrecisionValue = (metric: DynamicMetrics): string => {
    const value = metric.value;

    if (typeof value === "number") {
      if (metric.type === "duration") {
        // Show all time components down to seconds (days, hours, minutes, seconds)
        return formatDuration(value, {
          style: "long",
          maxParts: 6, // Show all parts
          includeSeconds: true, // Always include seconds
        });
      }
      if (metric.type === "percentage" || metric.type === "score") {
        // Show percentages with 2 decimal places
        return value.toFixed(2) + "%";
      }
      if (metric.type === "currency") {
        // Show currency with 2 decimal places
        return value.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }
      // For other numbers, show up to 4 decimal places if they exist
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      });
    }

    return String(value);
  };

  const getEnhancedTooltip = (metric: DynamicMetrics): ReactNode => {
    return (
      <>
        <div className="tooltip-header">
          {metricIcons[metric.type]}
          <span style={{ marginLeft: "8px" }}>{metric.label}</span>
        </div>
        <div
          style={{
            color: token.colorTextSecondary,
            lineHeight: "1.5",
          }}
        >
          {metric.tooltip}
        </div>
        <div className="tooltip-divider" />
        <div
          style={{
            background: token.colorFillSecondary,
            borderRadius: token.borderRadiusLG,
            padding: "8px 12px",
            fontFamily: token.fontFamilyCode,
            color: token.colorText,
          }}
        >
          Precise value: {metric.preciseValue || getFullPrecisionValue(metric)}
        </div>
      </>
    );
  };

  const renderMetricValue = (metric: DynamicMetrics) => {
    if (metric.type === "classification") {
      const value = String(metric.value);
      const color =
        classificationColors[value as keyof typeof classificationColors] ||
        token.colorPrimary;
      return <Tag color={color}>{value}</Tag>;
    }

    if (metric.type === "urgency") {
      const value = String(metric.value);
      const color =
        urgencyColors[value as keyof typeof urgencyColors] ||
        token.colorPrimary;
      return <Tag color={color}>{value}</Tag>;
    }

    return metric.value;
  };

  return (
    <StandardCard
      title="Metrics"
      className={className}
      style={{
        ...style,
        border: "none",
        boxShadow: "none",
        height: style?.height,
      }}
      bodyStyle={{
        padding: "0", // Ensure minimum height even when loading/empty
      }}
      headerStyle={{ display: "none" }}
    >
      <Row
        gutter={[16, 16]}
        style={{
          margin: 0,
          height: "100%",
          display: "flex",
          flexWrap: "nowrap",
          gap: "16px",
        }}
      >
        {displayMetrics.map((metric: DynamicMetrics) => {
          const icon = metricIcons[metric.type];
          const showProgress =
            metric.type === "score" || metric.type === "percentage";
          const progressPercent =
            showProgress && typeof metric.value === "number"
              ? metric.type === "score"
                ? metric.value
                : Math.min(100, Math.max(0, metric.value))
              : undefined;
          const suffix = metric.type === "percentage" ? "%" : undefined;

          return (
            <Col
              key={metric.key}
              {...colSpan}
              style={{
                display: "flex",
                padding: "0px",
                flex: 1,
                minWidth: 0,
              }}
            >
              <CustomTooltip
                content={getEnhancedTooltip(metric)}
                size="large"
                mouseEnterDelay={0.1}
                style={{ width: "100%" }}
              >
                <div style={{ width: "100%" }}>
                  <CompoundStatistic
                    title={metric.label}
                    value={renderMetricValue(metric)}
                    icon={icon}
                    type={metric.type}
                    showProgress={showProgress}
                    progressPercent={progressPercent}
                    suffix={suffix}
                    style={{
                      backgroundColor: token.colorBgLayout,
                      boxShadow: token.boxShadowTertiary,
                    }}
                  />
                </div>
              </CustomTooltip>
            </Col>
          );
        })}
      </Row>
    </StandardCard>
  );
};
